import React, { useMemo } from "react";
import {
  X,
  Trophy,
  Building2,
  Briefcase,
  Wallet,
  Target,
  ShieldCheck,
} from "lucide-react";

const SupplierStatsModal = ({ isOpen, onClose, supplier, projects }) => {
  if (!isOpen || !supplier) return null;

  const stats = useMemo(() => {
    // 1. Identify participation: Ensure we are matching the correct ID type
    const participation = projects.filter((p) =>
      p.sups?.some((s) => String(s.sid) === String(supplier.id))
    );

    // 2. Identify Selection: A project where this supplier IS the winner
    const winningSelections = participation.filter(
      (p) => String(p.winnerId) === String(supplier.id)
    );

    // 3. Identify True Wins (Invoiced):
    // We check for "invoiced" status OR the existence of an invoiceDate
    const invoicedWins = winningSelections.filter(
      (p) => p.status === "invoiced" || p.invoiceDate
    );

    // 4. Calculate Efficiency (True Wins / Total Selections)
    // If they are the winner of 1 project and it is invoiced, this WILL be 100%
    const winRate =
      winningSelections.length > 0
        ? ((invoicedWins.length / winningSelections.length) * 100).toFixed(1)
        : 0;

    // 5. Financials
    const totalRevenue = invoicedWins.reduce(
      (sum, p) => sum + (parseFloat(p.totalCost) || 0),
      0
    );

    const aov =
      invoicedWins.length > 0
        ? (totalRevenue / invoicedWins.length).toFixed(2)
        : "0.00";

    return {
      total: participation.length,
      wins: invoicedWins.length,
      winRate: parseFloat(winRate),
      aov,
      losses: participation.length - winningSelections.length,
    };
  }, [supplier.id, projects]); // Use supplier.id in dependency for precision

  const getLevelStyle = (level) => {
    const lvl = parseInt(level?.toString().replace(/\D/g, ""));
    // Level 1-2 == green
    if (lvl <= 2)
      return "bg-emerald-500/10 text-emerald-500 border-emerald-500/20";
    // Level 3-5 == orange
    if (lvl <= 5)
      return "bg-orange-500/10 text-orange-500 border-orange-500/20";
    // Level 6-8 == red
    return "bg-rose-500/10 text-rose-500 border-rose-500/20";
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 md:p-6 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-300">
      <div className="bg-white dark:bg-slate-900 w-full max-w-2xl rounded-[3rem] shadow-[0_32px_64px_-12px_rgba(0,0,0,0.2)] border border-white/20 overflow-hidden flex flex-col">
        {/* HEADER */}
        <div className="relative p-8 pb-12 overflow-hidden bg-slate-950">
          <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-600/20 rounded-full blur-[80px] -mr-32 -mt-32" />
          <div className="relative flex justify-between items-start">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-white/10 backdrop-blur-md rounded-2xl border border-white/10">
                  <Building2 className="text-white" size={24} />
                </div>
                <h2 className="text-3xl font-black text-white tracking-tight uppercase italic">
                  {supplier.name}
                </h2>
              </div>
              <div
                className={`inline-flex items-center px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.15em] border ${getLevelStyle(
                  supplier.beeLevel
                )}`}
              >
                B-BBEE {supplier.beeLevel}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-3 bg-white/5 hover:bg-rose-500/20 text-white/50 hover:text-rose-500 rounded-2xl transition-all border border-white/10"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* STATS HUB */}
        <div className="px-8 -mt-8 relative z-10 grid grid-cols-3 gap-4">
          <div className="bg-white dark:bg-slate-800 p-6 rounded-[2rem] shadow-xl border border-slate-100 dark:border-slate-700">
            <Trophy className="text-orange-500 mb-3" size={24} />
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
              Efficiency
            </p>
            <h4 className="text-2xl font-black text-slate-900 dark:text-white">
              {stats.winRate}%
            </h4>
          </div>
          <div className="bg-white dark:bg-slate-800 p-6 rounded-[2rem] shadow-xl border border-slate-100 dark:border-slate-700">
            <Briefcase className="text-indigo-500 mb-3" size={24} />
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
              Projects
            </p>
            <h4 className="text-2xl font-black text-slate-900 dark:text-white">
              {stats.total}
            </h4>
          </div>
          <div className="bg-white dark:bg-slate-800 p-6 rounded-[2rem] shadow-xl border border-slate-100 dark:border-slate-700">
            <Wallet className="text-emerald-500 mb-3" size={24} />
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
              Avg. Win
            </p>
            <h4 className="text-2xl font-black text-slate-900 dark:text-white">
              R{Math.round(stats.aov)}
            </h4>
          </div>
        </div>

        {/* DISTRIBUTION BAR */}
        <div className="p-8 space-y-8">
          <div>
            <div className="flex justify-between items-end mb-4">
              <h5 className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em]">
                Win/Loss Distribution
              </h5>
              <span className="text-[10px] font-bold text-slate-500">
                {stats.wins} Wins vs {stats.losses} Losses
              </span>
            </div>
            <div className="h-4 w-full bg-slate-100 dark:bg-slate-800 rounded-full flex overflow-hidden border-4 border-slate-100 dark:border-slate-800">
              <div
                className="h-full bg-orange-500 transition-all duration-1000"
                style={{ width: `${stats.winRate}%` }}
              />
              <div
                className="h-full bg-slate-300 dark:bg-slate-600"
                style={{ width: `${100 - stats.winRate}%` }}
              />
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="bg-slate-50 dark:bg-slate-800/50 p-6 flex justify-center border-t border-slate-100 dark:border-slate-800">
          <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.3em] flex items-center gap-3">
            <ShieldCheck size={14} className="text-indigo-500" /> QuoPro
            Intelligence Engine Active
          </p>
        </div>
      </div>
    </div>
  );
};

export default SupplierStatsModal;
 
